import {Helmet} from '@dr.pogodin/react-helmet';
import {differenceInDays} from 'date-fns';
import {DataStatus, FormSubmitHandler} from 'platform/components';

import {DeepPartial} from 'react-hook-form';
import {useParams} from 'react-router-dom';

import {useGetCustomerV2Query} from '@dms/api/customer';
import {
  useGetCorrectiveTaxDocumentForCreatingQuery,
  usePostCorrectiveTaxDocumentIssueMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {getCustomerName, handleApiError, useAddress} from '@dms/shared';

import {parseDate, useNavigate, useQueryState} from 'shared/web';

import {CreateCorrectiveTaxDocumentForm} from '../../components/CreateCorrectiveTaxDocumentForm';
import {AccountingCalculationCacheProvider} from '../../context/AccountingCalculationCacheProvider';
import {AccountingDocumentFormValues} from '../../types';
import {mapCustomer} from './utils/mapCustomer';
import {mapCustomFields} from './utils/mapCustomFields';
import {mapFormDataToRequestBody} from './utils/mapFormDataToRequestBody';
import {mapInvoiceItems} from './utils/mapInvoiceItems';
import {mapPaymentInfo} from './utils/mapPaymentInfo';

export function CreateCorrectiveTaxDocumentForAftersales() {
  return (
    <AccountingCalculationCacheProvider>
      <CreateCorrectiveTaxDocumentForAftersalesContent />
    </AccountingCalculationCacheProvider>
  );
}

function CreateCorrectiveTaxDocumentForAftersalesContent() {
  const {checkoutId, paymentId} = useParams();
  const [returnUrl] = useQueryState('returnUrl');
  const [correctiveTaxDocumentType] = useQueryState('correctiveTaxDocumentType');

  const navigate = useNavigate();

  const {composeAddress} = useAddress();

  const {
    isLoading: isCorrectiveTaxDocumentDetailsLoading,
    isError: isCorrectiveTaxDocumentDetailsError,
    data,
  } = useGetCorrectiveTaxDocumentForCreatingQuery({
    body: {
      correctiveTaxDocumentType:
        correctiveTaxDocumentType === 'full-correction' ||
        correctiveTaxDocumentType === 'partial-correction'
          ? correctiveTaxDocumentType
          : 'full-correction',
    },
    checkoutId: checkoutId!,
    paymentId: paymentId!,
  });
  const {
    data: additionalCustomer,
    isLoading: isAdditionalCustomerLoading,
    isError: isAdditionalCustomerError,
  } = useGetCustomerV2Query(
    {customerId: data?.additionalCustomer?.customerId ?? ''},
    {skip: !data?.additionalCustomer?.customerId}
  );

  const [postCorrectiveTaxDocumentIssue] = usePostCorrectiveTaxDocumentIssueMutation();

  const dueSinceIssueDateInDays =
    data?.dueDate &&
    data?.issueDate &&
    differenceInDays(parseDate(data.dueDate), parseDate(data.issueDate));

  const defaultValues: DeepPartial<AccountingDocumentFormValues> = {
    additionalCustomer: additionalCustomer && {
      contractInformationId: data?.additionalCustomer?.contractInformationId,
      customer: {
        addressLine1: composeAddress(additionalCustomer?.addresses[0]?.address),
        name: getCustomerName(additionalCustomer),
        cin: additionalCustomer?.businessInfo?.businessInfo?.registrationNumber,
        tin: additionalCustomer?.businessInfo?.businessInfo?.vatNumber,
        id: additionalCustomer?.id,
      },
      type: data?.additionalCustomer?.type as 'operator' | 'recipient',
    },
    currency: data?.currency,
    customer: mapCustomer(data?.customerId ?? '', data?.customerDetails),
    customFieldsPayload: mapCustomFields(data?.customFields),
    issueDate: data?.issueDate,
    dueSinceIssueDateInDays: dueSinceIssueDateInDays || 0,
    dueDate: data?.dueDate,
    dateOfTaxableSupply: data?.dateOfTaxableSupply,
    dateOfReception: data?.dateOfReception,
    paymentInfo: mapPaymentInfo(data?.paymentInfo),
    description: data?.description,
    invoiceItems: mapInvoiceItems(data?.items),
    documentSeriesId: data?.documentSeriesId,
    exchangeRateRatio: data?.exchangeRate
      ? {
          amount: data?.exchangeRate.amount.toString(),
          ratio: data?.exchangeRate.ratio.toString(),
          currency: data?.exchangeRate.currency,
        }
      : undefined,
    isExchangeRateRatioEnabled: !!data?.exchangeRate,
    footerNote: data?.behindItemsNote,
    isInternal: data?.invoiceCategory === 'internal',
  };

  const handleFormSubmit: FormSubmitHandler<AccountingDocumentFormValues> = (formData) =>
    postCorrectiveTaxDocumentIssue({
      checkoutId: checkoutId!,
      paymentId: paymentId!,
      body: mapFormDataToRequestBody(
        formData,
        data?.correctiveTaxDocumentType === 'full-correction'
      ),
    })
      .unwrap()
      .then(() => (returnUrl ? navigate(returnUrl) : navigate(-1)))
      .catch(handleApiError);

  const isLoading = isCorrectiveTaxDocumentDetailsLoading || isAdditionalCustomerLoading;
  const isError = isCorrectiveTaxDocumentDetailsError || isAdditionalCustomerError;

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight="80vh">
      <Helmet title={i18n.t('entity.correctiveTaxDocument.labels.newDocument')} />
      <CreateCorrectiveTaxDocumentForm
        defaultValues={defaultValues}
        handleFormSubmit={handleFormSubmit}
        dateOfTaxableSupply={data?.dateOfTaxableSupply}
        isListOfItemsDisabled={data?.correctiveTaxDocumentType === 'full-correction'}
        isFromCheckout
        isFull={data?.correctiveTaxDocumentType === 'full-correction'}
        invoiceType={data?.invoiceType}
      />
    </DataStatus>
  );
}
